import {
  Alert,
  Button,
  EmptyState,
  Field,
  Modal,
  PageHeader,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  useToast,
} from '@catering/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Play, Plus, Power } from 'lucide-react';
import { type JSX, useState } from 'react';

import { useCurrentUser } from '../../auth/use-current-user';
import { searchAllCustomers } from '../../customers/api';
import { GuideLink } from '../../guide/guide-link';
import {
  createOrderTemplate,
  generateOrdersFromTemplates,
  listOrderTemplates,
  updateOrderTemplate,
  DAY_OF_WEEK_LABELS,
  type OrderTemplate,
} from '../../orders/api';
import { mapOrderError } from '../../orders/order-api-errors';
import { hasPermission } from '../../auth/has-permission';
import {
  DEFAULT_ORDER_TEMPLATE_FORM_VALUES,
  toOrderTemplateCreateInput,
  toOrderTemplateFormValues,
  toOrderTemplateUpdateInput,
  type OrderTemplateFormValues,
} from '../../orders/order-template-form-schema';
import { OrderTemplateFormModal } from './components/order-template-form-modal';

type ModalState = { mode: 'create' } | { mode: 'edit'; template: OrderTemplate } | null;

const SKIPPED_REASON_LABELS: Record<string, string> = {
  ORDER_ALREADY_EXISTS: 'ya existía el pedido',
  NO_ACTIVE_CONTRACT: 'no hay contrato vigente',
  NO_ELIGIBLE_MENUS: 'no hay menús disponibles',
};

/** "Se generaron 3 pedidos. Se omitieron 2 (1 porque ya existía el pedido, 1 porque ...)." */
function generateSummary(result: { created: string[]; skipped: { reason: string }[] }): string {
  const created = `Se generaron ${result.created.length} pedidos`;

  if (result.skipped.length === 0) {
    return created;
  }

  const counts = new Map<string, number>();

  for (const { reason } of result.skipped) {
    counts.set(reason, (counts.get(reason) ?? 0) + 1);
  }

  const details = [...counts.entries()].map(
    ([reason, count]) =>
      `${count} porque ${SKIPPED_REASON_LABELS[reason] ?? 'no se pudo generar'}`,
  );

  return `${created}, se omitieron ${result.skipped.length} (${details.join(', ')})`;
}

function formatDays(daysOfWeek: number[]): string {
  if (daysOfWeek.length === 0) {
    return '—';
  }

  return [...daysOfWeek]
    .sort((a, b) => a - b)
    .map((day) => DAY_OF_WEEK_LABELS[day as keyof typeof DAY_OF_WEEK_LABELS] ?? String(day))
    .join(', ');
}

/**
 * `order-templates/spec.md` → "Pantalla de plantillas en back-office".
 * Read-only (no ABM, no generation) when the actor lacks
 * `order.template.manage` (spec's own "tabla se muestra en modo de solo
 * lectura, sin botones de ABM ni de generación").
 */
export function OrderTemplateListPage(): JSX.Element {
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [modal, setModal] = useState<ModalState>(null);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [generateDate, setGenerateDate] = useState('');

  const query = useQuery({ queryKey: ['order-templates'], queryFn: listOrderTemplates });

  const customersQuery = useQuery({
    queryKey: ['customers-for-order-templates'],
    queryFn: () => searchAllCustomers({ q: undefined, zone: undefined, status: undefined }),
  });

  const customerNames = Object.fromEntries(
    (customersQuery.data?.items ?? []).map((customer) => [customer.id, customer.legalName]),
  );

  async function invalidate(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['order-templates'] });
  }

  const createMutation = useMutation({
    mutationFn: (input: ReturnType<typeof toOrderTemplateCreateInput>) =>
      createOrderTemplate(input),
    onSuccess: async () => {
      await invalidate();
      setModal(null);
      toast('success', 'Plantilla creada');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: ReturnType<typeof toOrderTemplateUpdateInput>;
    }) => updateOrderTemplate(id, input),
    onSuccess: async () => {
      await invalidate();
      setModal(null);
      toast('success', 'Plantilla actualizada');
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: (template: OrderTemplate) =>
      updateOrderTemplate(template.id, { isActive: !template.isActive }),
    onSuccess: async (template) => {
      await invalidate();
      toast('success', template.isActive ? 'Plantilla activada' : 'Plantilla desactivada');
    },
  });

  const generateMutation = useMutation({
    mutationFn: (serviceDate: string) => generateOrdersFromTemplates(serviceDate),
    onSuccess: (result) => {
      setGenerateOpen(false);
      setGenerateDate('');
      toast('success', generateSummary(result));
    },
  });

  const canManage = hasPermission(currentUser.data, 'order.template.manage');

  // Raw error: `OrderTemplateFormModal` maps it itself. Passing an already
  // mapped string (`''` when there is no error) made the modal map it again
  // and always show the connection-error alert.
  const formError =
    modal?.mode === 'create'
      ? createMutation.error
      : modal?.mode === 'edit'
        ? updateMutation.error
        : undefined;

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        icon={Play}
        title="Plantillas de pedido"
        description="Gestioná pedidos recurrentes por empresa y sede, y generalos para una fecha con un clic."
        actions={
          <div className="flex items-center gap-2">
            {canManage ? (
              <>
                <Button variant="secondary" icon={Play} onClick={() => setGenerateOpen(true)}>
                  Generar pedidos ahora
                </Button>
                <Button icon={Plus} onClick={() => setModal({ mode: 'create' })}>
                  Nueva plantilla
                </Button>
              </>
            ) : null}
            <GuideLink moduleId="orders" />
          </div>
        }
      />

      {query.isError ? (
        <Alert tone="danger" title="No pudimos cargar las plantillas">
          <div className="flex items-center justify-between gap-4">
            <span>Ocurrió un error al buscar. Intentá de nuevo.</span>
            <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      {query.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : null}

      {query.data !== undefined && query.data.length === 0 ? (
        <EmptyState icon={Play} title="Todavía no hay plantillas cargadas." />
      ) : null}

      {query.data !== undefined && query.data.length > 0 ? (
        <Table>
          <caption className="sr-only">Plantillas de pedido</caption>
          <thead>
            <tr>
              <TableHeader>Nombre</TableHeader>
              <TableHeader>Empresa</TableHeader>
              <TableHeader>Días</TableHeader>
              <TableHeader>Estado</TableHeader>
              {canManage ? <TableHeader>Acciones</TableHeader> : null}
            </tr>
          </thead>
          <TableBody>
            {query.data.map((template) => (
              <TableRow key={template.id}>
                <TableCell className="font-medium text-slate-900">{template.name}</TableCell>
                <TableCell>{customerNames[template.customerId] ?? template.customerId}</TableCell>
                <TableCell>{formatDays(template.daysOfWeek)}</TableCell>
                <TableCell>{template.isActive ? 'Activa' : 'Inactiva'}</TableCell>
                {canManage ? (
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        icon={Pencil}
                        aria-label={`Editar ${template.name}`}
                        onClick={() => setModal({ mode: 'edit', template })}
                      >
                        Editar
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        icon={Power}
                        loading={
                          toggleActiveMutation.isPending &&
                          toggleActiveMutation.variables?.id === template.id
                        }
                        onClick={() => toggleActiveMutation.mutate(template)}
                      >
                        {template.isActive ? 'Desactivar' : 'Activar'}
                      </Button>
                    </div>
                  </TableCell>
                ) : null}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}

      {modal !== null ? (
        <OrderTemplateFormModal
          mode={modal.mode}
          defaultValues={
            modal.mode === 'edit'
              ? toOrderTemplateFormValues(modal.template)
              : DEFAULT_ORDER_TEMPLATE_FORM_VALUES
          }
          submitting={createMutation.isPending || updateMutation.isPending}
          error={formError}
          onClose={() => setModal(null)}
          onSubmit={async (values: OrderTemplateFormValues) => {
            if (modal.mode === 'create') {
              await createMutation
                .mutateAsync(toOrderTemplateCreateInput(values))
                .catch(() => undefined);
            } else {
              await updateMutation
                .mutateAsync({ id: modal.template.id, input: toOrderTemplateUpdateInput(values) })
                .catch(() => undefined);
            }
          }}
        />
      ) : null}

      {generateOpen ? (
        <Modal
          open
          onClose={() => setGenerateOpen(false)}
          title="Generar pedidos ahora"
          className="max-w-md"
        >
          <div className="flex flex-col gap-4">
            {generateMutation.isError ? (
              <Alert tone="danger">{mapOrderError(generateMutation.error)}</Alert>
            ) : null}
            <p className="text-sm text-slate-700">
              Se van a generar los pedidos de todas las plantillas activas que cubran la fecha
              elegida, salteando las combinaciones que ya tengan un pedido.
            </p>
            <Field
              label="Fecha de servicio"
              name="generateDate"
              type="date"
              required
              value={generateDate}
              onChange={(event) => setGenerateDate(event.target.value)}
            />
            <div className="flex items-center gap-3">
              <Button
                icon={Play}
                loading={generateMutation.isPending}
                disabled={generateDate === ''}
                onClick={() => generateMutation.mutate(generateDate)}
              >
                Generar
              </Button>
              <Button variant="secondary" onClick={() => setGenerateOpen(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}

      {toggleActiveMutation.isError ? (
        <Alert tone="danger">No pudimos cambiar el estado de la plantilla.</Alert>
      ) : null}
    </section>
  );
}
